/**
 * Security Utilities for XSS Prevention and Safe Data Handling
 * 
 * This module provides utilities to prevent common security vulnerabilities:
 * - Safe JSON parsing from localStorage/sessionStorage
 * - PostMessage origin validation
 * - URL validation for redirect protection
 */

/**
 * Safely parse JSON from localStorage/sessionStorage with validation
 * @param json - The JSON string to parse (can be null)
 * @param defaultValue - Default value to return if parsing fails or data is invalid
 * @returns Parsed data or default value
 */
export const safeJsonParse = <T>(json: string | null, defaultValue: T): T => {
  if (!json || json.trim() === '') {
    return defaultValue;
  }

  try {
    const parsed = JSON.parse(json);
    
    // Basic type validation - ensure parsed data matches expected structure
    if (typeof parsed === typeof defaultValue) {
      // For arrays, ensure it's actually an array
      if (Array.isArray(defaultValue)) {
        return (Array.isArray(parsed) ? parsed : defaultValue) as T;
      }
      
      // For objects, ensure it's not null and is an object
      if (typeof defaultValue === 'object' && defaultValue !== null) {
        return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed as T : defaultValue;
      }
      
      return parsed as T;
    }
    
    console.warn('safeJsonParse: Type mismatch, using default value');
    return defaultValue;
  } catch (error) {
    console.warn('safeJsonParse: Invalid JSON in storage:', error);
    return defaultValue;
  }
};

/**
 * Get trusted origins for PostMessage validation
 * @returns Array of trusted origins
 */
const getTrustedOrigins = (): string[] => {
  const origins = [
    window.location.origin,
    process.env.REACT_APP_AUTH_SERVER_URL,
    process.env.REACT_APP_API_BASE_URL,
  ].filter(Boolean) as string[];
  
  // Remove duplicates and ensure we have at least the current origin
  return Array.from(new Set(origins));
};

/**
 * Safely send PostMessage with origin validation
 * @param targetWindow - The window to send message to
 * @param message - The message to send
 * @param targetOrigin - Optional specific target origin (defaults to current origin)
 * @returns true if message was sent, false if validation failed
 */
export const safePostMessage = (
  targetWindow: Window,
  message: any,
  targetOrigin?: string
): boolean => {
  if (!targetWindow) {
    console.warn('safePostMessage: Target window is null');
    return false;
  }

  const trustedOrigins = getTrustedOrigins();
  const origin = targetOrigin || window.location.origin;
  
  if (!trustedOrigins.includes(origin)) {
    console.error('safePostMessage: Untrusted origin blocked:', origin);
    return false;
  }

  try {
    targetWindow.postMessage(message, origin);
    return true;
  } catch (error) {
    console.error('safePostMessage: Failed to send message:', error);
    return false;
  }
};

/**
 * Validate if a URL is safe for redirect (same origin only)
 * @param url - The URL to validate
 * @param allowedOrigins - Optional array of allowed origins (defaults to current origin only)
 * @returns true if URL is safe for redirect
 */
export const isValidRedirectUrl = (
  url: string,
  allowedOrigins?: string[]
): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);
    const allowed = allowedOrigins || [window.location.origin];
    
    return allowed.includes(urlObj.origin);
  } catch (error) {
    // Invalid URL format
    console.warn('isValidRedirectUrl: Invalid URL format:', url);
    return false;
  }
};

/**
 * Safely perform redirect with URL validation
 * @param url - The URL to redirect to
 * @param fallbackUrl - Fallback URL if validation fails (defaults to '/')
 * @param allowedOrigins - Optional array of allowed origins
 */
export const safeRedirect = (
  url: string,
  fallbackUrl: string = '/',
  allowedOrigins?: string[]
): void => {
  if (isValidRedirectUrl(url, allowedOrigins)) {
    window.location.href = url;
  } else {
    console.warn('safeRedirect: Blocked potentially unsafe redirect to:', url);
    console.log('safeRedirect: Redirecting to fallback URL:', fallbackUrl);
    window.location.href = fallbackUrl;
  }
};

/**
 * Validate and sanitize localStorage key names to prevent injection
 * @param key - The localStorage key to validate
 * @returns true if key is safe to use
 */
export const isValidStorageKey = (key: string): boolean => {
  if (!key || typeof key !== 'string') {
    return false;
  }
  
  // Allow only alphanumeric characters, underscores, hyphens, and dots
  const validKeyPattern = /^[a-zA-Z0-9_.-]+$/;
  return validKeyPattern.test(key) && key.length <= 100;
};

/**
 * Safely get item from localStorage with validation
 * @param key - The storage key
 * @param defaultValue - Default value if key doesn't exist or is invalid
 * @returns Parsed value or default
 */
export const safeGetStorageItem = <T>(key: string, defaultValue: T): T => {
  if (!isValidStorageKey(key)) {
    console.warn('safeGetStorageItem: Invalid storage key:', key);
    return defaultValue;
  }

  try {
    const item = localStorage.getItem(key);
    return safeJsonParse(item, defaultValue);
  } catch (error) {
    console.error('safeGetStorageItem: Error accessing localStorage:', error);
    return defaultValue;
  }
};

/**
 * Safely set item in localStorage with validation
 * @param key - The storage key
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export const safeSetStorageItem = (key: string, value: any): boolean => {
  if (!isValidStorageKey(key)) {
    console.warn('safeSetStorageItem: Invalid storage key:', key);
    return false;
  }

  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error('safeSetStorageItem: Error setting localStorage:', error);
    return false;
  }
};